import React, {useEffect, useState} from 'react'
import axios from 'axios'
import CustomSelect from '../../modules/custom-select/CustomSelect'

const API_URL = process.env.REACT_APP_API_URL
const GET_SEMESTERS_URL = `${API_URL}/Custom/GetSemesters`
const GET_FACULTIES_URL = `${API_URL}/Custom/GetFaculties`
const GET_DEPARTMENTS_URL = `${API_URL}/Custom/GetDepartmentsByFaculty`
const GET_INSTRUCTORS_URL = `${API_URL}/Custom/GetAllInstructors`
const GET_SCHEDULE_BY_INSTRUCTOR_URL = `${API_URL}/Custom/GetUniversityWeeklyScheduleByInstructor`
const GET_SCHEDULE_BY_CLASS_URL = `${API_URL}/Custom/GetUniversityWeeklyScheduleByClass`
const GET_BUILDINGS_URL = `${API_URL}/Custom/GetBuildings`
const GET_ROOMS_URL = `${API_URL}/Custom/GetRooms`
const GET_SCHEDULE_BY_CLASSROOM_URL = `${API_URL}/Custom/GetUniversityWeeklyScheduleByClassroom`

interface Option {
  id: number
  name: string
}

interface ScheduleItem {
  id: number
  day: number
  startTime: string
  endTime: string
  courseName: string
  courseCode?: string
  classroomName: string
  name?: string
  detail?: string
  mandatoryStudentCount?: number | null
  isEvent?: boolean
  eventDate?: string | null
}

const dayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar']
const dayColors = [
  'bg-primary',
  'bg-secondary',
  'bg-success',
  'bg-danger',
  'bg-warning',
  'bg-info',
  'bg-dark',
]
const dayNamesShort = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz']
const toMinutes = (time: string) => {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}

const UniversityCourseScheduleList: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'instructor' | 'class' | 'classroom'>('instructor')
  const [semesters, setSemesters] = useState<Option[]>([])
  const [selectedSemester, setSelectedSemester] = useState<number | null>(null)
  const [instructors, setInstructors] = useState<Option[]>([])
  const [selectedInstructor, setSelectedInstructor] = useState<number | null>(null)
  const [faculties, setFaculties] = useState<Option[]>([])
  const [selectedFaculty, setSelectedFaculty] = useState<number | null>(null)
  const [departments, setDepartments] = useState<Option[]>([])
  const [selectedDepartment, setSelectedDepartment] = useState<number | null>(null)
  const [selectedGrade, setSelectedGrade] = useState<number | null>(null)
  const [buildings, setBuildings] = useState<Option[]>([])
  const [selectedBuilding, setSelectedBuilding] = useState<number | null>(null)
  const [classrooms, setClassrooms] = useState<Option[]>([])
  const [selectedClassroom, setSelectedClassroom] = useState<number | null>(null)
  const [schedule, setSchedule] = useState<ScheduleItem[]>([])
  const [weekStart, setWeekStart] = useState<Date>(() => {
    const today = new Date()
    const dow = (today.getDay() + 6) % 7
    const start = new Date(today)
    start.setDate(today.getDate() - dow)
    start.setHours(0, 0, 0, 0)
    return start
  })

  useEffect(() => {
    axios
      .get(GET_SEMESTERS_URL)
      .then((res) => setSemesters(res.data || []))
      .catch(console.error)
  }, [])

  useEffect(() => {
    if (!selectedSemester) return
    if (activeTab === 'instructor') {
      axios
        .get(`${GET_INSTRUCTORS_URL}?semesterId=${selectedSemester}`)
        .then((res) => setInstructors(res.data || []))
        .catch(console.error)
      setSelectedInstructor(null)
      setFaculties([])
      setSelectedFaculty(null)
      setDepartments([])
      setSelectedDepartment(null)
      setSelectedGrade(null)
      setBuildings([])
      setSelectedBuilding(null)
      setClassrooms([])
      setSelectedClassroom(null)
      setSchedule([])
    } else if (activeTab === 'class') {
      axios
        .get(GET_FACULTIES_URL)
        .then((res) => setFaculties(res.data || []))
        .catch(console.error)
      setInstructors([])
      setSelectedInstructor(null)
      setSelectedFaculty(null)
      setDepartments([])
      setSelectedDepartment(null)
      setSelectedGrade(null)
      setBuildings([])
      setSelectedBuilding(null)
      setClassrooms([])
      setSelectedClassroom(null)
      setSchedule([])
    } else {
      axios
        .get(GET_BUILDINGS_URL)
        .then((res) => setBuildings(res.data || []))
        .catch(console.error)
      setInstructors([])
      setSelectedInstructor(null)
      setFaculties([])
      setSelectedFaculty(null)
      setDepartments([])
      setSelectedDepartment(null)
      setSelectedGrade(null)
      setSelectedBuilding(null)
      setClassrooms([])
      setSelectedClassroom(null)
      setSchedule([])
    }
    setWeekStart(() => {
      const today = new Date()
      const dow = (today.getDay() + 6) % 7
      const start = new Date(today)
      start.setDate(today.getDate() - dow)
      start.setHours(0, 0, 0, 0)
      return start
    })
  }, [selectedSemester, activeTab])

  useEffect(() => {
    if (activeTab !== 'class') return
    if (!selectedFaculty) {
      setDepartments([])
      setSelectedDepartment(null)
      setSelectedGrade(null)
      setSchedule([])
      return
    }
    axios
      .get(`${GET_DEPARTMENTS_URL}/${selectedFaculty}`)
      .then((res) => setDepartments(res.data || []))
      .catch(console.error)
    setSelectedDepartment(null)
    setSelectedGrade(null)
    setSchedule([])
  }, [selectedFaculty, activeTab])

  useEffect(() => {
    if (activeTab !== 'class') return
    setSelectedGrade(null)
    setSchedule([])
  }, [selectedDepartment, activeTab])

  useEffect(() => {
    if (activeTab !== 'classroom') return
    if (!selectedBuilding) {
      setClassrooms([])
      setSelectedClassroom(null)
      setSchedule([])
      return
    }
    axios
      .get(`${GET_ROOMS_URL}?buildingId=${selectedBuilding}&useExamCapacity=false`)
      .then((res) => setClassrooms(res.data || []))
      .catch(console.error)
    setSelectedClassroom(null)
    setSchedule([])
  }, [selectedBuilding, activeTab])

  const loadSchedule = () => {
    if (!selectedSemester) return
    if (activeTab === 'instructor' && selectedInstructor) {
      axios
        .get(
          `${GET_SCHEDULE_BY_INSTRUCTOR_URL}?semesterId=${selectedSemester}&instructorId=${selectedInstructor}`
        )
        .then((res) =>
          setSchedule(
            (res.data || []).map((i: any) => ({...i, eventDate: i.eventDate || i.EventDate || null}))
          )
        )
        .catch(console.error)
    } else if (activeTab === 'class' && selectedFaculty && selectedDepartment && selectedGrade) {
      axios
        .get(
          `${GET_SCHEDULE_BY_CLASS_URL}?semesterId=${selectedSemester}&classId=${selectedGrade}&departmentId=${selectedDepartment}`
        )
        .then((res) =>
          setSchedule(
            (res.data || []).map((i: any) => ({...i, eventDate: i.eventDate || i.EventDate || null}))
          )
        )
        .catch(console.error)
    } else if (activeTab === 'classroom' && selectedClassroom) {
      axios
        .get(
          `${GET_SCHEDULE_BY_CLASSROOM_URL}?semesterId=${selectedSemester}&classroomId=${selectedClassroom}`
        )
        .then((res) =>
          setSchedule(
            (res.data || []).map((i: any) => ({...i, eventDate: i.eventDate || i.EventDate || null}))
          )
        )
        .catch(console.error)
    }
  }

  const renderDefaultTable = () => {
    if (!schedule.length) return null
    const grouped = schedule.reduce((acc: Record<number, ScheduleItem[]>, item) => {
      if (!acc[item.day]) acc[item.day] = []
      acc[item.day].push(item)
      return acc
    }, {})
    return (
      <div className='row'>
        {Object.entries(grouped).map(([day, items]) => (
          <div key={day} className='col-md-6 col-lg-4 mb-4'>
            <h5 className={`p-2 rounded text-center text-white ${dayColors[Number(day) - 1]}`}>
              {dayNames[Number(day) - 1]}
            </h5>
            <table className='table table-bordered table-hover'>
              <thead className='table-light'>
                <tr>
                  <th className='text-center'>Saat</th>
                  <th>Ders</th>
                  <th>Tür</th>
                  <th>Sınıf</th>
                  {activeTab === 'instructor' && <th>Zorunlu Öğrenci Sayısı</th>}
                  <th>Açıklama</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id}>
                    <td className='text-center'>
                      <span className={`badge ${i.isEvent ? 'bg-warning text-dark' : 'bg-info'}`}>
                        {i.startTime} - {i.endTime}
                      </span>
                    </td>
                    <td>
                      {i.isEvent
                        ? i.courseName
                        : i.courseCode
                        ? `${i.courseName} (${i.courseCode})`
                        : i.courseName}
                    </td>
                    <td>{i.isEvent ? 'Etkinlik' : i.name ? i.name.split(' - ').pop() : ''}</td>
                    <td>{i.classroomName}</td>
                    {activeTab === 'instructor' && (
                      <td>{i.isEvent ? '-' : i.mandatoryStudentCount ?? '-'}</td>
                    )}
                    <td>
                      {i.detail || ''}
                      {i.isEvent && i.eventDate ? (
                        <div className='text-muted small'>{i.eventDate}</div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    )
  }

  const renderClassroomSchedule = () => {
    if (!schedule.length) return null
    const days: Date[] = Array.from({length: 7}, (_, i) => {
      const d = new Date(weekStart)
      d.setDate(weekStart.getDate() + i)
      return d
    })
    const formatDate = (d: Date) =>
      d.toLocaleDateString('tr-TR', {weekday: 'short', day: 'numeric', month: 'numeric'})
    const toKey = (d: Date) => {
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    const itemsForDate = (d: Date) => {
      const key = toKey(d)
      const dow = ((d.getDay() + 6) % 7) + 1
      return schedule
        .filter((s) => {
          if (s.eventDate) {
            const ev = (s.eventDate || '').split('T')[0]
            return ev === key
          }
          return s.day === dow
        })
        .sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime))
    }

    return (
      <div>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <button
            className='btn btn-light btn-sm'
            onClick={() =>
              setWeekStart((prev) => {
                const d = new Date(prev)
                d.setDate(d.getDate() - 7)
                return d
              })
            }
          >
            Önceki Hafta
          </button>
          <div className='fw-semibold'>
            {toKey(days[0])} - {toKey(days[days.length - 1])}
          </div>
          <button
            className='btn btn-light btn-sm'
            onClick={() =>
              setWeekStart((prev) => {
                const d = new Date(prev)
                d.setDate(d.getDate() + 7)
                return d
              })
            }
          >
            Sonraki Hafta
          </button>
        </div>
        <div className='row'>
          {days.map((d) => {
            const items = itemsForDate(d)
            return (
              <div key={toKey(d)} className='col-md-4 col-lg-3 mb-3'>
                <div className='border rounded h-100'>
                  <div className='p-2 bg-light fw-semibold text-center'>{formatDate(d)}</div>
                  <div className='p-2'>
                    {items.length === 0 && <div className='text-muted small'>Kayıt yok</div>}
                    {items.map((i, idx) => (
                      <div
                        key={idx}
                        className={`mb-2 p-2 border rounded ${i.isEvent ? 'border-warning bg-warning bg-opacity-10' : ''}`}
                      >
                        <div className='d-flex justify-content-between'>
                          <span className='fw-semibold'>
                            {i.isEvent
                              ? i.courseName
                              : i.courseCode
                              ? `${i.courseName} (${i.courseCode})`
                              : i.courseName}
                          </span>
                          <span className='badge bg-light text-dark border'>
                            {i.startTime} - {i.endTime}
                          </span>
                        </div>
                        <div className='small text-muted mt-1'>
                          {i.isEvent ? 'Etkinlik' : i.name ? i.name.split(' - ').pop() : ''}
                          {i.isEvent && i.eventDate ? ` • ${i.eventDate}` : ''}
                        </div>
                        {i.detail && <div className='small text-muted mt-1'>{i.detail}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderSchedule = () =>
    activeTab === 'classroom' ? renderClassroomSchedule() : renderDefaultTable()
  const buildingName = buildings.find((b) => b.id === selectedBuilding)?.name
  const classroomName = classrooms.find((c) => c.id === selectedClassroom)?.name

  return (
    <div className='container mt-4'>
      <style>{`@media print { @page { size: A4 landscape; margin: 10mm; } .schedule-print { font-size:10px; margin:0 !important; } .noprint { display: none !important; } }`}</style>
      <h2 className='mb-4'>
        {activeTab === 'classroom' && buildingName && classroomName
          ? `${buildingName} - ${classroomName} - Haftalık Ders Programı`
          : 'Üniversite Haftalık Ders Programı'}
      </h2>
      <div className='noprint'>
        <div className='mb-3'>
          <label>Dönem</label>
          <select
            className='form-control'
            value={selectedSemester || ''}
            onChange={(e) => setSelectedSemester(Number(e.target.value))}
          >
            <option value='' disabled>
              Seçiniz
            </option>
            {semesters.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <ul className='nav nav-tabs mb-3'>
          <li className='nav-item'>
            <button
              className={`nav-link ${activeTab === 'instructor' ? 'active' : ''}`}
              onClick={() => setActiveTab('instructor')}
            >
              Öğretim Elemanı
            </button>
          </li>
          <li className='nav-item'>
            <button
              className={`nav-link ${activeTab === 'class' ? 'active' : ''}`}
              onClick={() => setActiveTab('class')}
            >
              Sınıf
            </button>
          </li>
          <li className='nav-item'>
            <button
              className={`nav-link ${activeTab === 'classroom' ? 'active' : ''}`}
              onClick={() => setActiveTab('classroom')}
            >
              Derslik
            </button>
          </li>
        </ul>
        {activeTab === 'instructor' && (
          <div className='mb-3'>
            <label>Öğretim Elemanı</label>
            <CustomSelect
              options={instructors.map((i) => ({value: i.id, label: i.name}))}
              value={
                selectedInstructor
                  ? {
                      value: selectedInstructor,
                      label: instructors.find((i) => i.id === selectedInstructor)?.name || '',
                    }
                  : null
              }
              isSearchable
              onChange={(opt: any) => setSelectedInstructor(opt ? opt.value : null)}
            />
          </div>
        )}
        {activeTab === 'class' && (
          <>
            {faculties.length > 0 && (
              <div className='mb-3'>
                <label>Fakülte</label>
                <select
                  className='form-control'
                  value={selectedFaculty || ''}
                  onChange={(e) => setSelectedFaculty(Number(e.target.value))}
                >
                  <option value='' disabled>
                    Seçiniz
                  </option>
                  {faculties.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {departments.length > 0 && (
              <div className='mb-3'>
                <label>Bölüm</label>
                <select
                  className='form-control'
                  value={selectedDepartment || ''}
                  onChange={(e) => setSelectedDepartment(Number(e.target.value))}
                >
                  <option value='' disabled>
                    Seçiniz
                  </option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className='mb-3'>
              <label>Sınıf</label>
              <select
                className='form-control'
                value={selectedGrade || ''}
                onChange={(e) => setSelectedGrade(Number(e.target.value))}
              >
                <option value='' disabled>
                  Seçiniz
                </option>
                {[1, 2, 3, 4, 5, 6].map((g) => (
                  <option key={g} value={g}>
                    {g}. Sınıf
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
        {activeTab === 'classroom' && (
          <>
            {buildings.length > 0 && (
              <div className='mb-3'>
                <label>Bina</label>
                <select
                  className='form-control'
                  value={selectedBuilding || ''}
                  onChange={(e) => setSelectedBuilding(Number(e.target.value))}
                >
                  <option value='' disabled>
                    Seçiniz
                  </option>
                  {buildings.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {classrooms.length > 0 && (
              <div className='mb-3'>
                <label>Derslik</label>
                <select
                  className='form-control'
                  value={selectedClassroom || ''}
                  onChange={(e) => setSelectedClassroom(Number(e.target.value))}
                >
                  <option value='' disabled>
                    Seçiniz
                  </option>
                  {classrooms.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
        <div className='mb-4'>
          <button className='btn btn-primary me-2' onClick={loadSchedule}>
            Listele
          </button>
          {activeTab === 'instructor' && schedule.length > 0 && (
            <button
              className='btn btn-secondary'
              onClick={() => {
                const printUrl = `/program/print-instructor/${selectedSemester}/${selectedInstructor}`
                window.open(printUrl, '_blank', 'noopener,noreferrer')
              }}
            >
              PDF / Yazdır
            </button>
          )}
          {activeTab === 'class' && schedule.length > 0 && (
            <button
              className='btn btn-secondary'
              onClick={() => {
                const printUrl = `/program/print/${selectedSemester}/${selectedDepartment}/${selectedGrade}`
                window.open(printUrl, '_blank', 'noopener,noreferrer')
              }}
            >
              PDF / Yazdır
            </button>
          )}
          {activeTab === 'classroom' && schedule.length > 0 && (
            <button
              className='btn btn-secondary'
              onClick={() => {
                const printUrl = `/program/print-classroom/${selectedSemester}/${selectedClassroom}`
                window.open(printUrl, '_blank', 'noopener,noreferrer')
              }}
            >
              PDF / Yazdır
            </button>
          )}
        </div>
      </div>
      {renderSchedule()}
    </div>
  )
}

export default UniversityCourseScheduleList
